/**
 * AI Product Mapping — maps real sku-mapping SSE run frames onto the internal
 * grid row shape and rail-log entries.
 *
 * The matching pipeline streams per-row `match` / `nomatch` frames keyed by a
 * 0-based `lineIdx` (header row = -1). The grid keys rows by 1-based `idx`, so
 * `idx = lineIdx + 1` — the slice's batch merge then indexes them unchanged.
 *
 * Matched-product titles are NOT in the run contract (only SKU + ids), so rows
 * carry `matchedSku` + `matchSource`; title enrichment is a fast-follow.
 */

import { LOGICAL_FIELDS, REMATCH_REASON, ROW_STATUS } from './constants';
import { confidenceLevel } from './confidence';
import { rivalConfidence } from './matchPolicy';
import { mappedColumns } from './columnMapping';
import { seg, createRailEvent } from './eventFormat';
import type { ColumnResolution, MatchScores, RowType } from '../api/types';
import type {
  RunGroupCandidate,
  RunGroupDecisionState,
  RunGroupItem,
  RunGroupVerdict,
} from '../api/groupTypes';
import type { GridRow, RailEvent, RowCandidate, RowStatus, RunTotals } from './types';

/** A match/nomatch SSE frame's `data` payload (RUN_STREAM_CONTRACT). */
export interface MatchFrameAlternative {
  sku: string;
  productId?: string;
  variantId?: string;
  /** The candidate's PRODUCT title — absent on frames from older service builds. */
  title?: string | null;
  /** The candidate's catalog product type — absent on older frames. */
  productType?: string | null;
  /** The type lexicon's recognized noun — the type label fallback when productType is empty. */
  typeNoun?: string | null;
  /** Three-valued type verdict against the row (scores.typeAgreement semantics). */
  typeAgreement?: boolean | null;
  confidence?: number;
  /** Present only on a nomatch frame's top-ranked alternative. */
  scores?: MatchScores | null;
}
export interface RunFrameData {
  lineIdx: number;
  sku?: string;
  /** The committed candidate's catalog product (match frames; older frames omit it). */
  productId?: string;
  /** The committed candidate's PRODUCT title (match frames; older frames omit it). */
  title?: string | null;
  confidence?: number;
  source?: string;
  reason?: string;
  error?: string | null;
  /** Per-feature breakdown behind `confidence` — match frames only. */
  scores?: MatchScores | null;
  /** The row's product-type context (noun/source/presence) — both frame kinds. */
  type?: RowType | null;
  /** The row landed already confirmed: a human decided its product (a cached reviewer
   *  verdict for its group). Match frames only; older frames omit it. */
  confirmed?: boolean;
  alternatives?: MatchFrameAlternative[];
  fields?: Record<string, string>;
  row?: string[];
}

/** A row patch merged into a grid row by the slice (keyed by 1-based idx). */
export type RowPatch = Partial<GridRow> & { idx: number };

/** A `group_verdict` SSE frame's `data` payload (product-first funnel) — re-emitted with
 *  the same groupKey as stages firm the verdict up, so consumers upsert by key. */
export interface GroupVerdictFrameData {
  groupKey: string;
  label?: string;
  memberCount?: number;
  sampleCount?: number;
  verdict?: string;
  reason?: string | null;
  /** The product-type noun the absent-type gate judged the group on (null when none). */
  typeNoun?: string | null;
  /** The AI judge's one-line explanation (llm_* reasons only). */
  judgeReason?: string | null;
  /** True when the llm_* verdict was replayed from an earlier upload's cached judgement. */
  judgeCached?: boolean | null;
  /** The judge's own 0..1 answer confidence (llm_* reasons only). */
  judgeConfidence?: number | null;
  /** True while the judge may still speak, or while its decision is still being applied to
   *  the member rows — the group is not settled even when decisionState says it is. */
  awaitingJudge?: boolean;
  autoConfirmed?: boolean;
  candidates?: RunGroupCandidate[];
  funnel?: string | null;
  skuCoverageShare?: number | null;
  /** The group's first member row (0-based) — what the client bridges the server's own
   *  group key onto its client-side grouping with. Frames carry only this; the REST
   *  restore carries every member index. */
  /** Decision fields ride the verdict frame once the group resolves already decided
   *  (engine auto-confirm, cached reviewer verdict, type gate). Absent while pending —
   *  except on a server revert, which states "pending" explicitly to walk the client back. */
  decisionState?: string;
  chosenProductIds?: string[];
  decisionSource?: string | null;
}

/** A `group_state` SSE frame's `data` payload — a group's decision transition. Retired
 *  server-side (decisions now ride `group_verdict`); parsed for older deployments. */
export interface GroupStateFrameData {
  groupKey: string;
  decisionState?: string;
  chosenProductIds?: string[];
  decisionSource?: string | null;
}

/** An `awaiting_products` SSE frame's `data` payload — the park handoff (the stream
 *  closes; review continues over REST). */
export interface AwaitingProductsFrameData {
  runId?: string;
  totals?: Partial<RunTotals> | null;
  pendingGroups?: number;
}

const GROUP_VERDICTS: readonly RunGroupVerdict[] = ['pending', 'matched', 'candidates', 'none'];
const GROUP_DECISIONS: readonly RunGroupDecisionState[] = ['pending', 'confirmed', 'rejected', 'split'];

// A newer server may ship verdict values this build doesn't know — fold those to
// 'pending'/'pending' so the badge renders a neutral state instead of breaking the union.
// Exported so the REST mappers (restore read, decision response) fold identically.
export const normalizeVerdict = (verdict: string | undefined): RunGroupVerdict => {
  const v = String(verdict || '').toLowerCase() as RunGroupVerdict;
  return GROUP_VERDICTS.includes(v) ? v : 'pending';
};

export const normalizeDecision = (state: string | undefined): RunGroupDecisionState => {
  const v = String(state || '').toLowerCase() as RunGroupDecisionState;
  return GROUP_DECISIONS.includes(v) ? v : 'pending';
};

/** `group_verdict` frame → a full group upsert. Decision fields ride the frame only once
 *  the group is decided (absent = pending), so the slice's decided-guard still protects a
 *  client-side decision from a later judge re-emission. `decisionStated` records the
 *  difference for the reducer: a frame that STATES a decision — an explicit "pending"
 *  on a server revert included — is authoritative, an omitted one makes no claim. */
export function mapGroupVerdictEvent(data: GroupVerdictFrameData): RunGroupItem {
  return {
    groupKey: data.groupKey,
    decisionStated: data.decisionState != null,
    label: data.label || data.groupKey,
    memberCount: data.memberCount ?? 0,
    sampleCount: data.sampleCount ?? 0,
    verdict: normalizeVerdict(data.verdict),
    reason: data.reason ?? null,
    typeNoun: data.typeNoun ?? null,
    judgeReason: data.judgeReason ?? null,
    judgeCached: data.judgeCached ?? null,
    judgeConfidence: typeof data.judgeConfidence === 'number' ? data.judgeConfidence : null,
    awaitingJudge: Boolean(data.awaitingJudge),
    autoConfirmed: Boolean(data.autoConfirmed),
    candidates: Array.isArray(data.candidates) ? data.candidates : [],
    decisionState: normalizeDecision(data.decisionState),
    chosenProductIds: Array.isArray(data.chosenProductIds) ? data.chosenProductIds : [],
    decisionSource: data.decisionSource ?? null,
    funnel: data.funnel ?? null,
    skuCoverageShare: data.skuCoverageShare ?? null,
  };
}

/** `group_state` frame → the decision patch merged onto the group by key. */
export function mapGroupStateEvent(data: GroupStateFrameData): {
  groupKey: string;
  decisionState: RunGroupDecisionState;
  chosenProductIds: string[];
  decisionSource: string | null;
} {
  return {
    groupKey: data.groupKey,
    decisionState: normalizeDecision(data.decisionState),
    chosenProductIds: Array.isArray(data.chosenProductIds) ? data.chosenProductIds : [],
    decisionSource: data.decisionSource ?? null,
  };
}

// Within this top-1 vs best-alternative spread, a match is shown as ambiguous.
const AMBIGUOUS_SPREAD = 0.1;

function mapAlternatives(alternatives: MatchFrameAlternative[] = []): RowCandidate[] {
  return (alternatives || []).map((a) => ({
    sku: a.sku,
    productId: a.productId,
    variantId: a.variantId,
    confidence: a.confidence,
    conf: a.confidence, // grid/picker read `.conf`
    title: a.title ?? null,
    productType: a.productType ?? null,
    typeNoun: a.typeNoun ?? null,
    typeAgreement: a.typeAgreement ?? null,
    scores: a.scores ?? null,
  }));
}

// The server keys its per-frame display fields by the fully-LOWERCASED logical-field
// name ("productgroup"), while bindings and the grid columns use the camelCase key
// ("productGroup"). The two spellings coincide for every single-word field, so the
// mismatch only bites multi-word fields — normalize at the boundary so the grid,
// the restore path and the live stream all read one vocabulary.
const CANONICAL_FIELD_KEYS: Record<string, string> = Object.fromEntries(
  LOGICAL_FIELDS.map((f) => [f.key.toLowerCase(), f.key]),
);

function canonicalizeFieldKeys(fields: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(fields)) {
    out[CANONICAL_FIELD_KEYS[key.toLowerCase()] ?? key] = value;
  }
  return out;
}

// Server-driven grid: map the source row the server attaches to each frame onto
// the grid-row shape. `fields` is the matcher's extracted logical fields (already
// concatenated for multi-column Title/Category); `row` is the raw cells (for the
// CSV export). Keys are only included when present, so a patch never overwrites an
// existing value with `undefined` (keeps any already-seeded rows intact).
function sourcePatch(data: RunFrameData): Partial<Pick<GridRow, 'fields' | 'raw'>> {
  const patch: Partial<Pick<GridRow, 'fields' | 'raw'>> = {};
  if (data.fields) patch.fields = canonicalizeFieldKeys(data.fields);
  if (Array.isArray(data.row)) patch.raw = data.row;
  return patch;
}

/** `match` frame → row patch (keyed for the slice by 1-based idx). */
export function mapMatchEvent(data: RunFrameData): RowPatch {
  const confidence = data.confidence ?? 0;
  const candidates = mapAlternatives(data.alternatives);
  let status: RowStatus = confidenceLevel(confidence) === 'hi' ? ROW_STATUS.HI : ROW_STATUS.LOW;
  // A tie-break commit ALWAYS has a near-tied runner-up — its own displaced variant
  // sibling — so re-flagging it as ambiguous would undo exactly the decision the engine
  // made; the hover card explains the tie-break instead. A constrained re-match commit is
  // the same shape: every alternative is a sibling variant of the product the reviewer
  // chose, so the spread check has nothing to warn about. For plain matches, the spread
  // is measured against the best RIVAL-product candidate — a same-product sibling inside
  // the window is a variant nuance, not a product ambiguity.
  const rival = rivalConfidence(candidates, data.productId);
  if (
    data.reason !== 'variant_tie_break' &&
    data.reason !== REMATCH_REASON &&
    rival !== null &&
    confidence - rival <= AMBIGUOUS_SPREAD
  ) {
    status = ROW_STATUS.MULTI;
  }
  return {
    idx: data.lineIdx + 1,
    status,
    // The server says HOW it committed ("match" | "variant_tie_break"); older frames
    // carried no reason on matches, so the plain kind is the fallback.
    reason: data.reason || 'match',
    confidence,
    matchedSku: data.sku ?? null,
    matchedTitle: data.title ?? null,
    matchedProductId: data.productId ?? null,
    matchSource: data.source || null,
    scores: data.scores ?? null,
    rowType: data.type ?? null,
    candidates,
    errorMsg: null,
    // Only ever set TRUE from a frame: a replayed frame (reconnect) must not un-confirm a
    // row the reviewer confirmed in this session.
    ...(data.confirmed ? { confirmed: true } : {}),
    ...sourcePatch(data),
  };
}

// The server now sends the real no-match reason (below_threshold / ambiguous_top2 /
// no_candidates / row_error). Older servers flattened everything to below_threshold, so
// fall back to inferring a genuine dead end (no candidates) from an empty alternatives list.
function noMatchReason(data: RunFrameData): string {
  if (data.reason === 'row_error' || data.error) return 'row_error';
  if (data.reason && data.reason !== 'below_threshold') return data.reason;
  return (data.alternatives || []).length ? 'below_threshold' : 'no_candidates';
}

/** `nomatch` frame → row patch. `row_error`/a populated `error` becomes a row error. */
export function mapNoMatchEvent(data: RunFrameData): RowPatch {
  const isError = data.reason === 'row_error' || Boolean(data.error);
  const reason = noMatchReason(data);
  return {
    idx: data.lineIdx + 1,
    status: isError ? ROW_STATUS.ERROR : ROW_STATUS.NOMATCH,
    reason,
    confidence: 0,
    matchedSku: null,
    matchedTitle: null,
    matchSource: null,
    // No committed match to explain; the best guess's breakdown rides on candidates[0].
    scores: null,
    rowType: data.type ?? null,
    candidates: mapAlternatives(data.alternatives),
    errorMsg: isError ? data.error || 'Row could not be processed' : null,
    // A group rejection IS a reviewer's no-match verdict on every member row — render and
    // count these exactly like a hand-marked row (red chip, out of the needs-review tally)
    // instead of leaving them looking still-reviewable.
    ...(reason === 'rejected_no_match' ? { markedNoMatch: true } : {}),
    ...sourcePatch(data),
  };
}

// --- rail-log entries synthesized from real frames (keeps the event rail alive) ---

export const railRunStarted = (runId: string | null): RailEvent =>
  createRailEvent('ok', [seg.mono('event: run_started'), seg.plain(' · run '), seg.mono(runId || '—')]);

// Carries the full field→column mapping so the timeline item can expand to show
// it — this is the home of the mapping detail (there's no separate panel).
export const railColumnsResolved = (resolution: ColumnResolution | null | undefined): RailEvent => {
  const mapped = (resolution?.bindings || []).length;
  const total = (resolution?.headers || []).length;
  return createRailEvent(
    'info',
    [seg.mono('event: columns_resolved'), seg.plain(` · ${mapped} of ${total} columns mapped`)],
    { kind: 'columns', data: { columns: mappedColumns(resolution), mapped, total } },
  );
};

// Carries the confirmed (possibly user-edited) resolution under its own kind:
// the rail's pinned panel promotes the LATEST columns snapshot, so this replaces
// the server proposal with the end result while the marker text stays in the log.
export const railColumnsConfirmed = (resolution: ColumnResolution | null | undefined): RailEvent => {
  const mapped = (resolution?.bindings || []).length;
  const total = (resolution?.headers || []).length;
  return createRailEvent('ok', [seg.plain('Column mapping confirmed · processing resumed')], {
    kind: 'columns_confirmed',
    data: { columns: mappedColumns(resolution), mapped, total },
  });
};

export const railDone = (totals: Partial<RunTotals> = {}, truncated = false): RailEvent =>
  createRailEvent('ok', [
    seg.mono('event: done'),
    seg.plain(' · '),
    seg.strongNum(String(totals.matched ?? 0)),
    seg.plain(' matched · '),
    seg.num(String(totals.nomatch ?? 0)),
    seg.plain(' no-match · '),
    seg.num(String(totals.errors ?? 0)),
    seg.plain(' errors'),
    ...(truncated ? [seg.plain(' · '), seg.mono('truncated to 10k rows')] : []),
  ]);

export const railError = (code: string | null | undefined, message: string | null | undefined): RailEvent =>
  createRailEvent('err', [seg.mono(`event: ${code || 'error'}`), seg.plain(' · '), seg.plain(message || 'Run failed')]);

export const railCancelled = (): RailEvent =>
  createRailEvent('warn', [seg.mono('event: cancelled'), seg.plain(' · run stopped by user')]);

/** The park handoff: verdicts are in, the stream is closing, review continues over REST.
 *  Reads as COMPLETION — the engine's work is done, review is the user's own follow-up —
 *  so the raw event name stays out of the line. */
export const railAwaitingProducts = (pendingGroups: number, totals: Partial<RunTotals> = {}): RailEvent =>
  createRailEvent('ok', [
    seg.plain('Product matching complete · '),
    seg.strongNum(String(totals.matched ?? 0)),
    seg.plain(' rows matched · '),
    seg.num(String(pendingGroups)),
    seg.plain(pendingGroups === 1 ? ' group awaiting review' : ' groups awaiting review'),
  ]);

// Post-confirmation preparation stages, phrased for the reviewer. Anything unknown
// (a newer backend) falls back to a generic preparing line rather than hiding.
const STAGE_TEXT: Record<string, (processed: number, total: number | null) => string> = {
  // The server stage also tallies per-group type votes, but what the user watches it do
  // is collect the file's product groups — name the visible work, not the internals.
  analyzing_types: (p) =>
    p > 0 ? `Collecting product groups · ${p.toLocaleString()} rows scanned` : 'Collecting product groups…',
  classifying_types: (p, t) =>
    t ? `Classifying product groups with AI · ${p.toLocaleString()} / ${t.toLocaleString()}` : 'Classifying product groups with AI…',
  matching_products: (p, t) =>
    t ? `Matching products · ${p.toLocaleString()} / ${t.toLocaleString()} groups` : 'Matching products…',
  judging_products: (p, t) =>
    t
      ? `Double-checking undecided products with AI · ${p.toLocaleString()} / ${t.toLocaleString()}`
      : 'Double-checking undecided products with AI…',
  // The judge's own verdicts are in; this is the sweep that writes them onto member rows,
  // which is what the reviewer watches turn green. Counted in rows for that reason.
  applying_decisions: (p, t) =>
    t
      ? `Applying group decisions · ${p.toLocaleString()} / ${t.toLocaleString()} rows`
      : 'Applying group decisions…',
  // Row processing renders as ONE stage line updated in place — a 10k-row file ships
  // ~100 batch-boundary markers, which as individual lines drowned everything else.
  processing: (p, t) =>
    t ? `Processing rows · ${p.toLocaleString()} / ${t.toLocaleString()}` : `Processing rows · ${p.toLocaleString()}`,
  validating: () => 'Validating file…',
};

/** Human reading of a preparation-stage heartbeat — the progress strip's live status. */
export function describePrepStage(stage: { stage: string; processed: number; total: number | null }): string {
  const fmt = STAGE_TEXT[stage.stage];
  return fmt ? fmt(stage.processed, stage.total) : 'Preparing the run…';
}

/** One rail entry per preparation stage, updated in place as the stage advances so it
 *  settles on the stage's FINAL value (whole file scanned, all groups classified) instead
 *  of freezing at the first heartbeat. `kind`/`data.stage` let the reducer find and rewrite
 *  the existing line; the live per-heartbeat counter still ticks in the progress strip. */
export const railStage = (stage: string, processed: number, total: number | null): RailEvent =>
  createRailEvent(
    'info',
    [seg.mono(`stage: ${stage}`), seg.plain(` · ${describePrepStage({ stage, processed, total })}`)],
    { kind: 'stage', data: { stage, processed, total } },
  );
